import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { type CaixaLotteryType, CAIXA_LOTTERIES } from "@/utils/caixa";

interface FixedNumbersSelectorProps {
  lotteryType: CaixaLotteryType;
  maxFixedNumbers: number;
  onFixedNumbersChange: (numbers: number[]) => void;
  selectedNumbers: number[];
}

const FixedNumbersSelector: React.FC<FixedNumbersSelectorProps> = ({
  lotteryType,
  maxFixedNumbers,
  onFixedNumbersChange,
  selectedNumbers
}) => {
  const lottery = CAIXA_LOTTERIES[lotteryType];
  
  const getMaxNumber = () => {
    switch (lotteryType) {
      case "mega-sena": return 60;
      case "quina": return 80;
      case "lotomania": return 100;
      case "lotofacil": return 25;
      case "dupla-sena": return 50;
      case "dia-de-sorte": return 31;
      default: return 60;
    }
  };

  const maxNumber = getMaxNumber();
  const numbers = Array.from({ length: maxNumber }, (_, i) => i + 1);

  const handleNumberToggle = (number: number) => {
    if (selectedNumbers.includes(number)) {
      onFixedNumbersChange(selectedNumbers.filter(n => n !== number));
    } else if (selectedNumbers.length < maxFixedNumbers) {
      onFixedNumbersChange([...selectedNumbers, number].sort((a, b) => a - b));
    }
  };

  const clearSelection = () => {
    onFixedNumbersChange([]);
  };

  return (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-700">
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-blue-700 dark:text-blue-300">
              📌 Selecione até {maxFixedNumbers} dezenas fixas
            </h4>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-blue-100 text-blue-700">
                {selectedNumbers.length}/{maxFixedNumbers} selecionadas
              </Badge>
              {selectedNumbers.length > 0 && (
                <Button
                  onClick={clearSelection}
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Limpar
                </Button>
              )}
            </div>
          </div>

          {selectedNumbers.length > 0 && (
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
              <p className="text-sm font-medium mb-2">Dezenas fixas selecionadas:</p>
              <div className="flex flex-wrap gap-2">
                {selectedNumbers.map(number => (
                  <Badge
                    key={number}
                    className="bg-blue-600 text-white cursor-pointer hover:bg-blue-700"
                    onClick={() => handleNumberToggle(number)}
                  >
                    {number.toString().padStart(2, '0')} ✕
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-8 sm:grid-cols-10 md:grid-cols-12 lg:grid-cols-15 gap-2 max-h-64 overflow-y-auto">
            {numbers.map(number => {
              const isSelected = selectedNumbers.includes(number);
              const isDisabled = !isSelected && selectedNumbers.length >= maxFixedNumbers;
              
              return (
                <button
                  key={number}
                  onClick={() => handleNumberToggle(number)}
                  disabled={isDisabled}
                  className={`
                    w-8 h-8 rounded-full text-xs font-bold transition-all duration-200
                    ${isSelected 
                      ? 'bg-blue-600 text-white shadow-lg scale-110' 
                      : isDisabled
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-blue-50 hover:border-blue-300'
                    }
                  `}
                >
                  {number.toString().padStart(2, '0')}
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FixedNumbersSelector;
